import { FC, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import filter from 'lodash/filter';
import SwitchStep from './Stepper/SwitchStep';
import Stepper from 'src/components/Items/Stepper';
import InfoStepper from 'src/components/Items/InfoStepper';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { useActions } from 'src/hooks/useActions';
import {
  dineroAdd,
  dineroDivide,
  dineroGetPercentage,
  dineroGetSum,
  dineroMultiply,
  dineroSubtractAmount,
  rounder
} from 'src/common/utils/dataFormat';
import { scrollToTop } from 'src/common/utils/others';
import {
  DatesObj,
  findIntersectionInterval,
  getDuration,
  getExtraPrice
} from 'src/common/utils/seasonPriceLogic';
import { IOrder } from 'src/models/IOrder';
import { ISeasonPriceFetch } from 'src/models/ISeasonPrice';
import Logo from 'src/components/Items/Logo';
import './styles.scss';
import './media.scss';

const subId = process.env.REACT_APP_NODE_ENV ? 18 : 27;
// const nearId = 17; for stage
const nearId = 25;

const steps = [
  { label: 'Van & Dates', step: 0 },
  { label: 'Your info', step: 1 },
  { label: 'Reserve van', step: 2 }
];

export interface IPropsOrder {
  order: IOrder;
  onChange: (name: string, value: any) => void;
  setStep: (step: number) => void;
}

export type Order = Pick<IPropsOrder, 'order' | 'onChange'>;

const OrderPage: FC = () => {
  const { initOrder, changeOrder, getExtras, getSeasonPrice, getSubPrices } = useActions();
  const {
    order,
    extra: { extraList },
    subPrice: { subPriceList },
    van: {
      filters: { start_date, end_date, location, end_location, sub_location }
    },
    oneway: { onewayList },
    seasonPrice: { seasonPriceList }
  } = useTypedSelector(state => state);

  const [step, setStep] = useState(0);
  const [seasons, setSeasons] = useState<ISeasonPriceFetch[] | null>(null);

  const days = DateTime.fromISO(end_date!)
    .diff(DateTime.fromISO(start_date!), ['days'])
    .toObject().days;

  useEffect(() => {
    getExtras();
    getSeasonPrice();
    getSubPrices();
    return () => {
      initOrder();
      setStep(0);
    };
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [step]);

  useEffect(() => {
    if (seasonPriceList) {
      const selectedSeasons = seasonPriceList.filter(
        el =>
          el.location === location &&
          findIntersectionInterval(
            { start: start_date!, end: end_date! },
            { start: el.start_date, end: el.end_date }
          )
      );
      if (selectedSeasons.length) setSeasons(selectedSeasons);
    }
  }, [seasonPriceList]);

  useEffect(() => {
    if (extraList.length) {
      const defaultExtra = extraList.filter(ex => {
        return (
          ex.isDefault ||
          (rounder(days!) <= 1 && ex.id === 5) ||
          (rounder(days!) >= 7 && ex.id === 7) ||
          (seasons && ex.id === 6) ||
          (end_location && ex.id === 10) ||
          (sub_location && !end_location && ex.id === subId) ||
          (location !== order.van.location && ex.id === nearId)
        );
      });
      changeOrder('extras', defaultExtra);
    }
  }, [extraList, seasons]);

  useEffect(() => {
    const timeObject = DateTime.fromISO(end_date!)
      .diff(DateTime.fromISO(start_date!), ['days', 'hours'])
      .toObject();
    const { hours, days } = timeObject;

    let priceExtra = 0;
    let onewayPrice = 0;
    let summerPrice = 0;
    let discountPrice = 0;
    let vanPrice = dineroMultiply(order.van.price, timeObject.days!);

    if (hours && days) {
      if (hours <= 6 && hours > 0) {
        vanPrice = dineroAdd(vanPrice, dineroDivide(order.van?.price!, 4), order.currency!);
      } else if (hours > 6 && hours <= 12) {
        vanPrice = dineroAdd(vanPrice, dineroDivide(order.van?.price!, 2), order.currency!);
      } else if (hours > 12 && hours <= 18) {
        vanPrice = dineroAdd(
          vanPrice,
          dineroSubtractAmount(
            order.van?.price!,
            dineroDivide(order.van?.price!, 4),
            order.currency!
          ),
          order.currency!
        );
      } else if (hours > 18 && hours <= 24) {
        vanPrice = dineroAdd(vanPrice, order.van?.price!, order.currency!);
      }
    }

    if (!vanPrice) vanPrice = order.van.price;

    if (end_location) {
      onewayPrice = filter(onewayList, {
        start_location: location,
        end_location
      })[0].price;

      const oneway = order.extras.filter(i => i.id === 10)[0];
      if (oneway) {
        oneway.price = onewayPrice;
        oneway.title = oneway.title.includes(end_location)
          ? oneway.title
          : `${oneway.title} ${end_location}`;
      }
    }
    order.extras.forEach(i => {
      if (i.id === 6 || i.id === 7 || i.id === 16) i.price = 0;
      else if (i.id === subId) {
        const sublocation = order.van.host.sublocation_price.filter(i => i.location === location);
        if (sublocation.length) {
          i.price = sublocation[0].price;
        } else if (subPriceList.length) {
          const sublocation = subPriceList.filter(sub => sub.location === sub_location);
          if (sublocation.length) {
            i.price = sublocation[0].price;
          } else {
            const extras = order.extras.filter(i => i.id !== subId);
            changeOrder('extras', extras);
          }
        }
      } else if (i.id === nearId) {
        const near = order.van.near_van.find(nv => nv.location === location);
        if (near) i.price = near.price;
      }
    });
    order.extras.forEach(extra => (priceExtra += extra.id !== 10 ? extra.price : 0));

    let fullPrice = priceExtra + vanPrice;
    if (seasons && order.price) {
      const summerExtra = order.extras.filter(i => i.id === 6);
      if (summerExtra.length) {
        if (seasons.length > 1) {
          const bookingObj = DatesObj(start_date!, end_date!, 0);
          seasons.forEach(el => {
            const seasonObj = DatesObj(el.start_date, el.end_date, el.rate);
            Object.keys(seasonObj).forEach(key => {
              if (key in bookingObj) {
                bookingObj[key] =
                  bookingObj[key] > seasonObj[key] ? bookingObj[key] : seasonObj[key];
              }
            });
          });
          Object.entries(bookingObj).forEach(([key, value]) => {
            const price = dineroMultiply(
              dineroGetPercentage(order.van.price, Math.abs(value)),
              Math.sign(value)
            );
            summerPrice += price;
          });
        } else {
          const duration = getDuration(
            { start: seasons[0].start_date, end: seasons[0].end_date },
            { start: start_date!, end: end_date! }
          );
          summerPrice = getExtraPrice(order.van.price, duration, seasons[0].rate);
        }
        summerExtra[0].price = summerPrice;
      }
    }

    fullPrice = dineroGetSum(fullPrice, summerPrice);
    if (days && order.price) {
      const discountExtra = order.extras.find(i => i.id === 7);
      if (discountExtra) {
        if (days === 7) {
          discountPrice = discountExtra!.price = -dineroGetPercentage(fullPrice, 5);
        } else if (days > 7 && days <= 21) {
          discountPrice = discountExtra!.price = -dineroGetPercentage(fullPrice, 10);
        } else if (days > 21 && days <= 30) {
          discountPrice = discountExtra!.price = -dineroGetPercentage(fullPrice, 20);
        } else if (days > 30) {
          discountPrice = discountExtra!.price = -dineroGetPercentage(fullPrice, 27);
        }
      }
    }
    fullPrice = dineroGetSum(fullPrice, discountPrice);
    const fullPriceWithOneway = dineroGetSum(fullPrice, onewayPrice);
    const cardFeeExtra = order.extras.filter(i => i.id === 16);
    const cardFee = dineroGetPercentage(fullPriceWithOneway, 3);
    cardFeeExtra.length && (cardFeeExtra[0].price = cardFee);
    changeOrder('price', dineroGetSum(fullPriceWithOneway, cardFee));
  }, [order.extras, order.extras.length, order.van, end_date, start_date, seasons, subPriceList]);

  return (
    <div className='order'>
      <div className='order__header'>
        <Logo />
        <Stepper activeStep={step} steps={steps.map(step => step.label)} />
        <InfoStepper />
      </div>
      <div className={step !== 2 ? 'order__wrapper' : ''}>
        <SwitchStep step={step} setStep={setStep} />
      </div>
    </div>
  );
};

export default OrderPage;
